import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { kebabCase } from "lodash";
import Accordion from "react-bootstrap/Accordion";
import { RlNavContext } from "./RlNav";
import RlNavItemInnerContent from "./RlNavItemInnerContent";
import { sanitizeTextForTestId } from "../../../utils";

const RlNavAccordionItem = ({
  navKey,
  text,
  textClassName,
  label,
  children,
  icon,
  iconClassName,
  expanded = false,
  topLevel = false,
  topLevelStatic = false,
  image,
  link,
  linkState,
  helpText,
  "data-testid": dataTestId,
  show = true,
  onSelectOpenFirstChild = false
}) => {
  const { activeKey } = useContext(RlNavContext);
  const activeNavItem =
    (activeKey === navKey || activeKey === link) && !onSelectOpenFirstChild;
  const isChildActive = useMemo(
    () =>
      React.Children.toArray(children).reduce(
        (acc, child) => acc || child.props?.to === activeKey,
        false
      ),
    [activeKey]
  );

  const isExpanded = expanded || isChildActive;
  const accordionKey = useMemo(() => kebabCase(text), []); // Use mounted text as key, we want it to stay static
  const defaultAccordionActiveKey =
    topLevel || topLevelStatic || isExpanded ? accordionKey : null; // This will show the item expanded by default;
  const hasChildren = !!children;
  const isBasicDropdownItem = hasChildren && (!topLevel || !topLevelStatic);
  const getItemClassNameModifier = () =>
    (topLevel && "top-level") ||
    (topLevelStatic && "top-level-static") ||
    (isBasicDropdownItem && "dropdown") ||
    "basic";

  const itemClassNames = `rl-nav__item rl-nav__item--${getItemClassNameModifier()} ${
    activeNavItem && !topLevel ? "rl-nav__item--active" : ""
  }`;

  if (!show) {
    return null;
  }

  return (
    <>
      <Accordion
        defaultActiveKey={defaultAccordionActiveKey}
        bsPrefix="rl-nav__accordion"
      >
        <RlNavItemInnerContent
          topLevel={topLevel}
          topLevelStatic={topLevelStatic}
          accordionKey={accordionKey}
          className={itemClassNames}
          icon={icon}
          iconAdditionalClassName={iconClassName}
          text={text}
          textClassName={textClassName}
          label={label}
          hasChildren={hasChildren}
          image={image}
          link={link}
          linkState={linkState}
          helpText={helpText}
          data-testid={dataTestId || sanitizeTextForTestId(text)}
          onSelectOpenFirstChild={onSelectOpenFirstChild}
        >
          {text}
        </RlNavItemInnerContent>
        <Accordion.Collapse eventKey={accordionKey}>
          <div className="rl-nav__submenu">{children}</div>
        </Accordion.Collapse>
      </Accordion>
    </>
  );
};

RlNavAccordionItem.propTypes = {
  navKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  textClassName: PropTypes.string,
  label: PropTypes.node,
  children: PropTypes.node,
  icon: PropTypes.string,
  iconClassName: PropTypes.string,
  expanded: PropTypes.bool,
  topLevel: PropTypes.bool,
  topLevelStatic: PropTypes.bool,
  show: PropTypes.bool,
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string
  }),
  link: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  linkState: PropTypes.any,
  helpText: PropTypes.string,
  "data-testid": PropTypes.string,
  onSelectOpenFirstChild: PropTypes.bool
};

export default RlNavAccordionItem;
